/**
 * Turns whatever the listener is hearing into spectrum data.
 *
 * The station's audio arrives through a plain media element (hls.js on the
 * landing player, the console stage, or a future browser DJ session), so the
 * tap is a `MediaElementSource` wired `element → analyser → destination`: the
 * element's own sound keeps flowing to the speakers through the same node,
 * which is what keeps playback and visuals locked to the identical buffer.
 *
 * AudioContexts need a user gesture on most browsers, so the context is created
 * lazily from a play handler and resumed on the first pointer/key event as a
 * belt-and-braces fallback.
 */

/** Coarse bands handed to the shaders; 32 maps cleanly onto two `vec4` rows. */
export const BIN_COUNT = 32;

export interface SpectrumTap {
  readonly analyser: AnalyserNode;
  /** 32 log-spaced bands in 0..1. */
  readonly levels: Float32Array;
  /** Time-domain trace in 0..1 (0.5 is silence), for waveform modes. */
  readonly wave: Float32Array;
  /** Band energies in 0..1. */
  bass: number;
  mid: number;
  treble: number;
  level: number;
  /** True once real audio has been seen at least once. */
  readonly heard: boolean;
  /** Highest raw analyser byte in the last frame (0..255), for diagnostics. */
  readonly rawPeak: number;
  /** Pull the latest frame. Returns false while the analyser is still empty. */
  update(): boolean;
}

let context: AudioContext | null = null;
const taps = new WeakMap<HTMLMediaElement, SpectrumTap>();

function ensureContext(): AudioContext | null {
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!context) {
    context = new Ctor();
    const resume = (): void => {
      if (context && context.state !== "running") void context.resume().catch(() => undefined);
    };
    window.addEventListener("pointerdown", resume, { passive: true });
    window.addEventListener("keydown", resume);
  }
  if (context.state === "suspended") void context.resume().catch(() => undefined);
  return context;
}

/** AudioContext state, for diagnostics in the console. */
export function audioContextState(): string {
  return context?.state ?? "none";
}

/**
 * Route `media` through an analyser and hand back the tap.
 *
 * Safe to call repeatedly — the element is only ever routed once, because
 * `createMediaElementSource` cannot be undone and calling it twice throws.
 */
export function attachSpectrum(media: HTMLMediaElement): SpectrumTap | null {
  const ctx = ensureContext();
  if (!ctx) return null;

  const existing = taps.get(media);
  if (existing) {
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    return existing;
  }

  try {
    const source = ctx.createMediaElementSource(media);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.8;
    // Terminal branch: nothing downstream, so analysis costs no extra output.
    source.connect(analyser);
    analyser.connect(ctx.destination);
    const tap = createTap(analyser);
    taps.set(media, tap);
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    return tap;
  } catch {
    // Already routed, or the element is not playable yet.
    return taps.get(media) ?? null;
  }
}

function createTap(analyser: AnalyserNode): SpectrumTap {
  const spectrum = new Uint8Array(analyser.frequencyBinCount);
  const wave = new Uint8Array(analyser.fftSize);
  const levels = new Float32Array(BIN_COUNT);
  const trace = new Float32Array(64);
  const binCount = analyser.frequencyBinCount;
  const nyquist = (analyser.context.sampleRate || 48_000) / 2;

  // Log-spaced bands from 40 Hz to 12 kHz: bass gets real resolution instead
  // of the six low bins a linear split would give it.
  const lowHz = 40;
  const highHz = Math.min(12_000, nyquist * 0.9);
  const edges = new Int32Array(BIN_COUNT + 1);
  for (let i = 0; i <= BIN_COUNT; i += 1) {
    const hz = lowHz * Math.pow(highHz / lowHz, i / BIN_COUNT);
    edges[i] = Math.max(1, Math.min(binCount, Math.round((hz / nyquist) * binCount)));
  }

  let heard = false;
  let rawPeak = 0;

  return {
    analyser,
    levels,
    wave: trace,
    bass: 0,
    mid: 0,
    treble: 0,
    level: 0,
    get heard() {
      return heard;
    },
    get rawPeak() {
      return rawPeak;
    },
    update(): boolean {
      analyser.getByteFrequencyData(spectrum);
      analyser.getByteTimeDomainData(wave);

      let raw = 0;
      for (let bin = 0; bin < spectrum.length; bin += 1) {
        const value = spectrum[bin] ?? 0;
        if (value > raw) raw = value;
      }
      rawPeak = raw;

      let peak = 0;
      let sum = 0;
      for (let band = 0; band < BIN_COUNT; band += 1) {
        const from = edges[band] ?? 1;
        const to = Math.max(from + 1, edges[band + 1] ?? binCount);
        let acc = 0;
        for (let bin = from; bin < to; bin += 1) acc += spectrum[bin] ?? 0;
        const value = acc / (to - from) / 255;
        levels[band] = value;
        peak = Math.max(peak, value);
        sum += value;
      }

      const mean = sum / BIN_COUNT;
      this.bass = average(levels, 0, 6);
      this.mid = average(levels, 6, 20);
      this.treble = average(levels, 20, BIN_COUNT);
      this.level = Math.max(mean, peak * 0.6);
      if (peak > 0.02) heard = true;

      // 64-point decimated trace for waveform drawing.
      const step = Math.floor(wave.length / trace.length);
      for (let i = 0; i < trace.length; i += 1) {
        trace[i] = (wave[i * step] ?? 128) / 255;
      }

      return peak > 0;
    },
  };
}

function average(values: Float32Array, from: number, to: number): number {
  let acc = 0;
  let count = 0;
  for (let i = from; i < to; i += 1) {
    acc += values[i] ?? 0;
    count += 1;
  }
  return count === 0 ? 0 : acc / count;
}
